import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { Animation } from '@babylonjs/core/Animations/animation';
import '@babylonjs/loaders/glTF';

// All clips share the Mixamo skeleton ("mixamorig:*" transform nodes), so
// retargeting is a rename-free target swap onto the character's own nodes.
//
// Every clip is split into TWO AnimationGroups at load time:
//   .L — hips/spine/legs (locomotion layer)
//   .U — spine1 and up: chest, head, arms, hands (action layer)
// Full-body playback runs both; masked playback mixes one clip's legs with
// another clip's upper body (attack/aim/carry while walking in any stance).

// Upper layer owns the whole torso (all Spine bones) so aim/carry poses
// reproduce faithfully; lower layer keeps hips + legs for locomotion.
const UPPER_RE = /Spine|Neck|Head|Shoulder|Arm|Hand/;

const LOOP = [
  // core locomotion (walk set + crouch set are the original deliveries)
  ['idle', 'idle-standing'], ['fight-idle', 'fight-idle'],
  ['walk', 'walking'], ['walk_back', 'walking-backwards'],
  ['walk_left', 'walk-left'], ['walk_right', 'walk-right'],
  ['walk_fl', 'walk-forward-left'], ['walk_bl', 'walk-backward-left'], ['walk_br', 'walk-backward-right'],
  ['run', 'run'], ['run_fl', 'run-fl'], ['run_fr', 'run-fr'],
  ['run_left', 'run-left'], ['run_right', 'run-right'],
  ['run_back', 'run-back'], ['run_bl', 'run-bl'], ['run_br', 'run-br'],
  ['sprint', 'sprint'],
  ['crouch_idle', 'crouch-idle'], ['crouch_fwd', 'rifle-crouch-walk'],
  ['crouch_left', 'walk-crouching-left'], ['crouch_right', 'walk-crouching-right'],
  ['crouch_back', 'walk-crouching-backward'], ['crouch_run', 'crouched-run'],
  ['prone_fwd', 'prone-fwd'], ['prone_back', 'prone-back'],
  // holds (upper-body carry/aim/block poses — played as .U over any legs)
  ['block-idle', 'block-idle'],
  ['knife-idle', 'knife-idle'], ['bat-idle', 'bat-idle'],
  ['spear-idle', 'spear-idle'], ['axe-idle', 'axe-idle'], ['axe-block', 'axe-block'],
  ['pistol-idle', 'pistol-idle'], ['pistol-kneel', 'pistol-kneel-idle'],
  ['rifle-idle', 'rifle-idle'], ['rifle-kneel', 'rifle-kneel-idle'],
  // full-body aim set (aim clips' spines assume their own bladed hips, so
  // they can't ride the upper layer — aiming swaps the whole base instead)
  ['rifle-aim', 'rifle-aim-idle'],
  ['aim-walk-fwd', 'aim-walk-fwd'], ['aim-walk-back', 'aim-walk-back'],
  ['aim-walk-left', 'aim-walk-left'], ['aim-walk-right', 'aim-walk-right'],
  ['crouch_aim', 'idle-crouching-aiming'],
];

const ONCE = [
  // stance transitions
  ['stand_to_crouch', 'standing-to-crouch'], ['crouch_to_stand', 'crouch-to-standing'],
  ['crouch_to_prone', 'crouch-to-prone'], ['prone_to_crouch', 'prone-to-crouch'],
  // jumps / rolls / dodges
  ['jump', 'jump'], ['jump_running', 'jump-running'], ['rifle-jump', 'rifle-jump'],
  ['vault', 'vault'], ['climb-top', 'climb-top'],
  ['roll-fwd', 'roll-fwd'], ['roll-sprint', 'roll-sprint'],
  ['dodge-back', 'dodge-back'], ['dodge-right', 'dodge-right'], ['prone-roll', 'prone-roll'],
  // unarmed strikes
  ['jab', 'jab'], ['cross', 'cross'], ['jab-cross', 'jab-cross'],
  ['hook-l', 'hook-l'], ['hook-r', 'hook-r'], ['uppercut', 'uppercut'],
  ['elbow-l', 'elbow-l'], ['elbow-r', 'elbow-r'], ['headbutt', 'headbutt'],
  ['kick-round', 'kick-round'], ['kick-low', 'kick-low'], ['leg-sweep', 'leg-sweep'],
  ['knee-lead', 'knee-lead'], ['kick-high', 'kick-high'], ['kick-flying', 'kick-flying'],
  ['jump-attack', 'jump-attack'],
  ['combo-punch', 'combo-punch'], ['punch-elbow-combo', 'punch-elbow-combo'], ['quad-punch', 'quad-punch'],
  ['body-blow', 'body-blow'], ['block-react', 'block-react'],
  // knife / spear
  ['knife-stab', 'knife-stab'], ['knife-thrust', 'knife-thrust'], ['knife-slash-adv', 'knife-slash-adv'],
  ['slash-inward', 'slash-inward'], ['slash-outward', 'slash-outward'], ['spear-stab', 'spear-stab'],
  // bat / club swings
  ['club-1h', 'club-1h-combo'], ['club-2h', 'club-2h-combo'],
  ['melee-horizontal', 'melee-horizontal'], ['melee-downward', 'melee-downward'],
  ['melee-backhand', 'melee-backhand'], ['melee-360-high', 'melee-360-high'],
  ['melee-360-low', 'melee-360-low'], ['melee-combo-2', 'melee-combo-2'], ['melee-combo-3', 'melee-combo-3'],
  // axe
  ['axe-slash', 'axe-slash'], ['axe-attack', 'axe-attack'], ['axe-spin', 'axe-spin'],
  ['axe-jump-attack', 'axe-jump-attack'], ['axe-kick', 'axe-kick'],
  ['axe-draw', 'axe-draw'], ['heavy-swing', 'heavy-swing'],
  // guns
  ['pistol-fire', 'pistol-fire'], ['pistol-draw', 'pistol-draw'], ['reload-standing', 'reload-standing'],
  ['rifle-fire', 'rifle-fire'], ['rifle-reload', 'rifle-reload'],
  ['rifle-prone-fire', 'rifle-prone-fire'], ['rifle-prone-reload', 'rifle-prone-reload'],
  ['rifle-punch', 'rifle-punch'], ['rifle-butt', 'rifle-butt'],
  ['rifle-equip', 'rifle-equip'], ['rifle-holster', 'rifle-holster'],
  ['rifle-down-to-aim', 'rifle-down-to-aim'],
  // deaths
  ['death', 'death'], ['death_front', 'death-from-the-front'],
  ['death_back_hs', 'death-from-back-headshot'], ['death_front_hs', 'death-from-front-headshot'],
  ['death_right', 'death-from-right'], ['death_fwd', 'standing-death-forward-01'],
  ['crouch_death', 'crouch-death'], ['prone_death', 'prone-death'],
];

// Split-retarget an AnimationGroup onto same-named nodes. Hips XZ translation
// is stripped so clips play in place (controller owns world movement).
function retargetSplit(group, targetMap, scene, name) {
  const L = new AnimationGroup(name + '#L', scene);
  const U = new AnimationGroup(name + '#U', scene);
  let fps = 30, maxFrame = 0;
  for (const ta of group.targetedAnimations) {
    const targetName = ta.target?.name;
    const target = targetMap.get(targetName);
    if (!target) continue;
    let anim = ta.animation;
    if (targetName === 'mixamorig:Hips' && anim.targetProperty === 'position') {
      anim = anim.clone();
      for (const key of anim.getKeys()) { key.value.x = 0; key.value.z = 0; }
    }
    fps = anim.framePerSecond || fps;
    const keys = anim.getKeys();
    if (keys.length) maxFrame = Math.max(maxFrame, keys[keys.length - 1].frame);
    (UPPER_RE.test(targetName) ? U : L).addTargetedAnimation(anim, target);
  }
  const dur = maxFrame / fps;
  return { L, U, dur };
}

// Static pose loop from one frame of a source clip (prone idle, aim holds).
// pick: 'first' or 'last' key of each track.
function freezeFrame(src, scene, name, pick = 'first') {
  const out = {};
  for (const half of ['L', 'U']) {
    const g = new AnimationGroup(name + '#' + half, scene);
    for (const ta of src[half].targetedAnimations) {
      const keys = ta.animation.getKeys();
      const v = (pick === 'last' ? keys[keys.length - 1] : keys[0]).value;
      const anim = new Animation(name, ta.animation.targetProperty, 30,
        ta.animation.dataType, Animation.ANIMATIONLOOPMODE_CYCLE);
      anim.setKeys([{ frame: 0, value: v }, { frame: 60, value: v }]);
      g.addTargetedAnimation(anim, ta.target);
    }
    out[half] = g;
  }
  out.dur = 2;
  return out;
}

export async function loadClips(scene, characterNodes, onProgress) {
  const targetMap = new Map();
  for (const n of characterNodes) targetMap.set(n.name, n);

  const defs = [
    ...LOOP.map(([name, file]) => ({ name, file, loop: true })),
    ...ONCE.map(([name, file]) => ({ name, file, loop: false })),
  ];
  const clips = {};
  let done = 0;
  const queue = [...defs];
  async function worker() {
    for (let d = queue.shift(); d; d = queue.shift()) {
      try {
        const c = await LoadAssetContainerAsync(`assets/anims/mixamo/${d.file}.glb`, scene);
        const clip = retargetSplit(c.animationGroups[0], targetMap, scene, d.name);
        clip.loop = d.loop;
        clips[d.name] = clip;
        c.dispose();
      } catch (e) {
        console.warn('clip failed:', d.name, e.message);
      }
      onProgress?.(++done, defs.length, d.name);
    }
  }
  await Promise.all(Array.from({ length: 8 }, worker));

  if (clips.prone_fwd) clips.prone_idle = { ...freezeFrame(clips.prone_fwd, scene, 'prone_idle'), loop: true };
  if (clips['rifle-prone-fire'])
    clips['rifle-prone-aim'] = { ...freezeFrame(clips['rifle-prone-fire'], scene, 'rifle-prone-aim'), loop: true };
  // "Shooting Pistol" opens on a clean two-handed aim — freeze it as the
  // full-body pistol ADS pose (the dedicated aim clips all point off-axis)
  if (clips['pistol-fire'])
    clips['pistol-aim'] = { ...freezeFrame(clips['pistol-fire'], scene, 'pistol-aim', 'first'), loop: true };
  return clips;
}
